import React, { useState, useEffect, useRef } from 'react';
import api from '../../utils/api';

const $c = (amt) => `$${parseFloat(amt || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const $d = (iso) => iso ? new Date(iso).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : '—';

function AgingBar({ aging, total }) {
  if (!total) return null;
  const bands = [
    { label: 'Current',   key: 'current', color: '#22c55e' },
    { label: '1–30 days', key: '1-30',    color: '#f59e0b' },
    { label: '31–60',     key: '31-60',   color: '#f97316' },
    { label: '61–90',     key: '61-90',   color: '#ef4444' },
    { label: '90+',       key: '90+',     color: '#991b1b' },
  ];
  return (
    <div style={{ marginBottom: 24 }}>
      <div style={{ display: 'flex', height: 10, borderRadius: 6, overflow: 'hidden', background: 'var(--border)', marginBottom: 8 }}>
        {bands.map(b => {
          const pct = ((aging[b.key] || 0) / total) * 100;
          if (!pct) return null;
          return <div key={b.key} style={{ width: `${pct}%`, background: b.color }} title={`${b.label}: ${$c(aging[b.key])}`} />;
        })}
      </div>
      <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap', fontSize: 11, color: 'var(--text-secondary)' }}>
        {bands.map(b => (
          <span key={b.key} style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
            <span style={{ display: 'inline-block', width: 10, height: 10, borderRadius: 2, background: b.color, flexShrink: 0 }} />
            {b.label}: <strong style={{ color: 'var(--text-primary)' }}>{$c(aging[b.key])}</strong>
          </span>
        ))}
      </div>
    </div>
  );
}

export default function AccountStatement() {
  const [customers, setCustomers] = useState([]);
  const [loadingCustomers, setLoadingCustomers] = useState(true);
  const [search, setSearch] = useState('');
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [selected, setSelected] = useState(null);
  const [statement, setStatement] = useState(null);
  const [generating, setGenerating] = useState(false);
  const [error, setError] = useState(null);
  const [expandedOrders, setExpandedOrders] = useState({});
  const dropdownRef = useRef(null);

  useEffect(() => {
    api.get('/api/b2b/statement/customers')
      .then(({ data }) => setCustomers(data))
      .catch(e => setError(e.response?.data?.error || e.message))
      .finally(() => setLoadingCustomers(false));
  }, []);

  // Close dropdown on outside click
  useEffect(() => {
    const handler = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const customerName = (c) => {
    if (!c) return '';
    const full = [c.firstName, c.lastName].filter(Boolean).join(' ');
    if (c.company && full) return `${c.company} — ${full}`;
    return c.company || full || c.email || `Customer ${c.id}`;
  };

  const filtered = customers.filter(c => {
    const q = search.toLowerCase();
    if (!q) return true;
    return customerName(c).toLowerCase().includes(q) || (c.email || '').toLowerCase().includes(q);
  });

  const generate = async (customer) => {
    setSelected(customer);
    setDropdownOpen(false);
    setSearch('');
    setStatement(null);
    setError(null);
    setGenerating(true);
    setExpandedOrders({});
    try {
      const { data } = await api.post('/api/b2b/statement/generate', {
        customerId: customer.id,
        accountId: customer.accountId,
        storeUrl: customer.storeUrl,
      });
      setStatement(data);
    } catch (e) {
      setError(e.response?.data?.error || e.message);
    } finally {
      setGenerating(false);
    }
  };

  const statusColor = (daysOverdue) => {
    if (daysOverdue <= 0) return { bg: '#f0fdf4', text: '#166534', label: 'Current' };
    if (daysOverdue <= 30) return { bg: '#fffbeb', text: '#92400e', label: `${daysOverdue}d overdue` };
    if (daysOverdue <= 60) return { bg: '#fff7ed', text: '#9a3412', label: `${daysOverdue}d overdue` };
    return { bg: '#fef2f2', text: '#991b1b', label: `${daysOverdue}d overdue` };
  };

  return (
    <>
      <style>{`
        @media print {
          body * { visibility: hidden !important; }
          #statement-printable, #statement-printable * { visibility: visible !important; }
          #statement-printable { position: absolute; top: 0; left: 0; width: 100%; padding: 24px; background: white; }
          .no-print { display: none !important; }
          .invoice-row { page-break-inside: avoid; }
          table { page-break-inside: auto; }
          tr { page-break-inside: avoid; page-break-after: auto; }
          thead { display: table-header-group; }
        }
      `}</style>

      <div style={{ padding: '20px 28px', maxWidth: 1100, margin: '0 auto' }}>

        {/* Customer picker */}
        <div className="no-print" style={{ marginBottom: 24, display: 'flex', alignItems: 'flex-end', gap: 12 }}>
          <div style={{ flex: 1, maxWidth: 420 }}>
            <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: 'var(--text-secondary)', marginBottom: 6 }}>
              B2B CUSTOMER
            </label>
            <div ref={dropdownRef} style={{ position: 'relative' }}>
              <div
                onClick={() => { setDropdownOpen(o => !o); }}
                style={{
                  display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                  padding: '8px 12px', border: '1px solid var(--border)', borderRadius: 6,
                  background: 'var(--surface-1)', cursor: 'pointer', fontSize: 13,
                  color: selected ? 'var(--text-primary)' : 'var(--text-tertiary)',
                  minHeight: 38,
                }}
              >
                <span>{selected ? customerName(selected) : (loadingCustomers ? 'Loading customers…' : 'Select a customer…')}</span>
                <i className={`ti ti-chevron-${dropdownOpen ? 'up' : 'down'}`} style={{ fontSize: 12, color: 'var(--text-secondary)', flexShrink: 0, marginLeft: 8 }} />
              </div>

              {dropdownOpen && (
                <div style={{
                  position: 'absolute', top: '100%', left: 0, right: 0, zIndex: 100,
                  background: '#fff', border: '1px solid var(--border)',
                  borderRadius: 6, boxShadow: '0 4px 16px rgba(0,0,0,0.2)',
                  marginTop: 4, maxHeight: 320, display: 'flex', flexDirection: 'column',
                }}>
                  <div style={{ padding: 8, borderBottom: '1px solid var(--border)' }}>
                    <input
                      autoFocus
                      value={search}
                      onChange={e => setSearch(e.target.value)}
                      placeholder="Search name, company, email…"
                      style={{
                        width: '100%', boxSizing: 'border-box', padding: '6px 10px',
                        border: '1px solid var(--border)', borderRadius: 5,
                        fontSize: 12, background: '#f5f5f5',
                      }}
                    />
                  </div>
                  <div style={{ overflowY: 'auto', flex: 1 }}>
                    {filtered.length === 0 ? (
                      <div style={{ padding: '14px 12px', fontSize: 12, color: 'var(--text-tertiary)', textAlign: 'center' }}>
                        {customers.length === 0 ? 'No B2B-tagged customers found' : 'No matches'}
                      </div>
                    ) : filtered.map(c => (
                      <div
                        key={`${c.accountId}-${c.id}`}
                        onClick={() => generate(c)}
                        style={{
                          padding: '9px 12px', cursor: 'pointer', fontSize: 13,
                          background: selected?.id === c.id && selected?.accountId === c.accountId
                            ? 'var(--primary-light, #eff6ff)' : 'transparent',
                          borderBottom: '1px solid var(--border-subtle, #f0f0f0)',
                        }}
                        onMouseEnter={e => e.currentTarget.style.background = '#f5f5f5'}
                        onMouseLeave={e => e.currentTarget.style.background =
                          selected?.id === c.id && selected?.accountId === c.accountId
                            ? 'var(--primary-light, #eff6ff)' : 'transparent'}
                      >
                        <div style={{ fontWeight: 500 }}>{customerName(c)}</div>
                        <div style={{ fontSize: 11, color: 'var(--text-tertiary)', marginTop: 1 }}>
                          {c.email}{c.accountName ? ` · ${c.accountName}` : ''}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Error */}
        {error && (
          <div style={{ background: '#fef2f2', border: '1px solid #fecaca', borderRadius: 6, padding: '10px 14px', fontSize: 13, color: '#991b1b', marginBottom: 16 }}>
            {error}
          </div>
        )}

        {/* Empty state */}
        {!selected && !generating && !error && (
          <div style={{ textAlign: 'center', paddingTop: 80, color: 'var(--text-tertiary)' }}>
            <i className="ti ti-file-description" style={{ fontSize: 48, display: 'block', marginBottom: 12 }} />
            <div style={{ fontSize: 15 }}>Select a B2B customer to generate their account statement</div>
          </div>
        )}

        {/* Loading */}
        {generating && (
          <div style={{ textAlign: 'center', paddingTop: 80, color: 'var(--text-secondary)' }}>
            <div style={{ fontSize: 13 }}>Fetching open invoices from Shopify…</div>
          </div>
        )}

        {/* Statement */}
        {statement && !generating && (
          <div id="statement-printable">
            {/* Header */}
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 24, flexWrap: 'wrap', gap: 12 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
                <img src="/images/summit_logo.png" alt="Summit Fishing Equipment" style={{ height: 48, width: 'auto' }} />
                <div>
                  <h1 style={{ fontSize: 22, fontWeight: 700, margin: 0 }}>Account Statement</h1>
                  <div style={{ fontSize: 12, color: 'var(--text-secondary)', marginTop: 4 }}>
                    Generated {$d(statement.generatedAt)} &nbsp;·&nbsp; {statement.accountName}
                  </div>
                </div>
              </div>
              <button className="btn btn-sm no-print" onClick={() => window.print()}>
                <i className="ti ti-printer" /> Print / PDF
              </button>
            </div>

            {/* Customer + summary cards */}
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, marginBottom: 24 }}>
              <div style={{ background: 'var(--surface-1)', border: '1px solid var(--border)', borderRadius: 8, padding: '16px 20px' }}>
                <div style={{ fontSize: 11, fontWeight: 600, color: 'var(--text-secondary)', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 8 }}>Bill To</div>
                {statement.customer.company && <div style={{ fontWeight: 700, fontSize: 15 }}>{statement.customer.company}</div>}
                <div style={{ fontSize: 14 }}>{[statement.customer.firstName, statement.customer.lastName].filter(Boolean).join(' ')}</div>
                {statement.customer.email && <div style={{ fontSize: 12, color: 'var(--text-secondary)', marginTop: 4 }}>{statement.customer.email}</div>}
              </div>

              <div style={{ background: 'var(--surface-1)', border: '1px solid var(--border)', borderRadius: 8, padding: '16px 20px' }}>
                <div style={{ fontSize: 11, fontWeight: 600, color: 'var(--text-secondary)', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 8 }}>Balance Summary</div>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
                  <div>
                    <div style={{ fontSize: 11, color: 'var(--text-secondary)' }}>Total Outstanding</div>
                    <div style={{ fontSize: 20, fontWeight: 700, color: statement.summary.totalOutstanding > 0 ? '#dc2626' : 'var(--text-primary)' }}>
                      {$c(statement.summary.totalOutstanding)}
                    </div>
                  </div>
                  <div>
                    <div style={{ fontSize: 11, color: 'var(--text-secondary)' }}>Overdue</div>
                    <div style={{ fontSize: 20, fontWeight: 700, color: statement.summary.totalOverdue > 0 ? '#dc2626' : '#16a34a' }}>
                      {$c(statement.summary.totalOverdue)}
                    </div>
                  </div>
                  <div>
                    <div style={{ fontSize: 11, color: 'var(--text-secondary)' }}>Current (not yet due)</div>
                    <div style={{ fontSize: 15, fontWeight: 600 }}>{$c(statement.summary.totalCurrent)}</div>
                  </div>
                  <div>
                    <div style={{ fontSize: 11, color: 'var(--text-secondary)' }}>Open invoices</div>
                    <div style={{ fontSize: 15, fontWeight: 600 }}>{statement.summary.invoiceCount}</div>
                  </div>
                </div>
              </div>
            </div>

            {/* Aging bar */}
            <AgingBar aging={statement.summary.aging} total={statement.summary.totalOutstanding} />

            {/* Invoice table */}
            {statement.invoices.length === 0 ? (
              <div style={{ textAlign: 'center', padding: '40px 0', color: 'var(--text-tertiary)', fontSize: 14 }}>
                No outstanding invoices — this customer is fully paid up.
              </div>
            ) : (
              <div style={{ background: 'var(--surface-1)', border: '1px solid var(--border)', borderRadius: 8, overflow: 'hidden' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                  <thead>
                    <tr style={{ background: 'var(--surface-2)', borderBottom: '1px solid var(--border)' }}>
                      <th style={{ textAlign: 'left', padding: '10px 14px', fontWeight: 600, fontSize: 11, color: 'var(--text-secondary)', textTransform: 'uppercase' }}>Invoice</th>
                      <th style={{ textAlign: 'left', padding: '10px 14px', fontWeight: 600, fontSize: 11, color: 'var(--text-secondary)', textTransform: 'uppercase' }}>PO #</th>
                      <th style={{ textAlign: 'left', padding: '10px 14px', fontWeight: 600, fontSize: 11, color: 'var(--text-secondary)', textTransform: 'uppercase' }}>Order Date</th>
                      <th style={{ textAlign: 'left', padding: '10px 14px', fontWeight: 600, fontSize: 11, color: 'var(--text-secondary)', textTransform: 'uppercase' }}>Due Date</th>
                      <th style={{ textAlign: 'left', padding: '10px 14px', fontWeight: 600, fontSize: 11, color: 'var(--text-secondary)', textTransform: 'uppercase' }}>Status</th>
                      <th style={{ textAlign: 'right', padding: '10px 14px', fontWeight: 600, fontSize: 11, color: 'var(--text-secondary)', textTransform: 'uppercase' }}>Invoice Total</th>
                      <th style={{ textAlign: 'right', padding: '10px 14px', fontWeight: 600, fontSize: 11, color: 'var(--text-secondary)', textTransform: 'uppercase' }}>Outstanding</th>
                      <th className="no-print" style={{ width: 32 }} />
                    </tr>
                  </thead>
                  <tbody>
                    {statement.invoices.map((inv, idx) => {
                      const sc = statusColor(inv.daysOverdue);
                      const isExpanded = expandedOrders[inv.orderId];
                      return (
                        <React.Fragment key={inv.orderId}>
                          <tr className="invoice-row"
                            style={{ borderBottom: '1px solid var(--border-subtle, #f0f0f0)', background: idx % 2 === 0 ? 'transparent' : 'var(--surface-2)' }}>
                            <td style={{ padding: '10px 14px', fontWeight: 600, fontSize: 11, whiteSpace: 'nowrap' }}>
                              <a href={inv.adminUrl} target="_blank" rel="noreferrer"
                                style={{ color: 'var(--primary)', textDecoration: 'none' }}>
                                {inv.orderName}
                              </a>
                            </td>
                            <td style={{ padding: '10px 14px', color: 'var(--text-secondary)', fontSize: 12 }}>{inv.poNumber || '—'}</td>
                            <td style={{ padding: '10px 14px', color: 'var(--text-secondary)' }}>{$d(inv.orderDate)}</td>
                            <td style={{ padding: '10px 14px', fontWeight: inv.daysOverdue > 0 ? 600 : 'normal', color: inv.daysOverdue > 0 ? '#dc2626' : 'var(--text-primary)' }}>
                              {$d(inv.dueDate)}
                            </td>
                            <td style={{ padding: '10px 14px' }}>
                              <span style={{ fontSize: 11, padding: '3px 8px', borderRadius: 4, background: sc.bg, color: sc.text, fontWeight: 600 }}>
                                {sc.label}
                              </span>
                            </td>
                            <td style={{ padding: '10px 14px', textAlign: 'right', color: 'var(--text-secondary)' }}>{$c(inv.totalPrice)}</td>
                            <td style={{ padding: '10px 14px', textAlign: 'right', fontWeight: 700, color: inv.daysOverdue > 0 ? '#dc2626' : 'var(--text-primary)' }}>
                              {$c(inv.totalOutstanding)}
                            </td>
                            <td className="no-print" style={{ padding: '10px 8px', textAlign: 'center' }}>
                              <button onClick={() => setExpandedOrders(p => ({ ...p, [inv.orderId]: !p[inv.orderId] }))}
                                style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-secondary)', fontSize: 14, padding: 2 }}>
                                <i className={`ti ${isExpanded ? 'ti-chevron-up' : 'ti-chevron-down'}`} />
                              </button>
                            </td>
                          </tr>
                          {isExpanded && inv.lineItems?.length > 0 && (
                            <tr className="no-print">
                              <td colSpan={7} style={{ padding: '0 14px 12px 28px', background: 'var(--surface-3, #f8f9fa)' }}>
                                <table style={{ width: '100%', fontSize: 12, borderCollapse: 'collapse' }}>
                                  <thead>
                                    <tr>
                                      <th style={{ textAlign: 'left', padding: '6px 8px', color: 'var(--text-secondary)', fontWeight: 600 }}>SKU</th>
                                      <th style={{ textAlign: 'left', padding: '6px 8px', color: 'var(--text-secondary)', fontWeight: 600 }}>Product</th>
                                      <th style={{ textAlign: 'right', padding: '6px 8px', color: 'var(--text-secondary)', fontWeight: 600 }}>Qty</th>
                                      <th style={{ textAlign: 'right', padding: '6px 8px', color: 'var(--text-secondary)', fontWeight: 600 }}>Unit Price</th>
                                      <th style={{ textAlign: 'right', padding: '6px 8px', color: 'var(--text-secondary)', fontWeight: 600 }}>Line Total</th>
                                    </tr>
                                  </thead>
                                  <tbody>
                                    {inv.lineItems.map((li, i) => (
                                      <tr key={i} style={{ borderTop: '1px solid var(--border-subtle, #e5e7eb)' }}>
                                        <td style={{ padding: '5px 8px', fontFamily: 'monospace', fontSize: 11 }}>{li.sku || '—'}</td>
                                        <td style={{ padding: '5px 8px' }}>{li.title}</td>
                                        <td style={{ padding: '5px 8px', textAlign: 'right' }}>{li.quantity}</td>
                                        <td style={{ padding: '5px 8px', textAlign: 'right' }}>{$c(li.price)}</td>
                                        <td style={{ padding: '5px 8px', textAlign: 'right', fontWeight: 600 }}>{$c(li.lineTotal)}</td>
                                      </tr>
                                    ))}
                                  </tbody>
                                </table>
                              </td>
                            </tr>
                          )}
                        </React.Fragment>
                      );
                    })}
                  </tbody>
                </table>
                {/* Total row OUTSIDE the table so it only prints once (tfoot repeats on every page) */}
                <div style={{ display: 'flex', justifyContent: 'flex-end', borderTop: '2px solid var(--border)', background: 'var(--surface-2)', padding: '12px 14px', gap: 32 }}>
                  <span style={{ fontWeight: 700, fontSize: 13 }}>Invoice Total</span>
                  <span style={{ minWidth: 100, textAlign: 'right', color: 'var(--text-secondary)', fontSize: 13 }}>
                    {$c(statement.invoices.reduce((s, i) => s + i.totalPrice, 0))}
                  </span>
                  <span style={{ fontWeight: 700, fontSize: 13 }}>Total Outstanding</span>
                  <span style={{ minWidth: 100, textAlign: 'right', fontWeight: 700, fontSize: 15, color: '#dc2626' }}>
                    {$c(statement.summary.totalOutstanding)}
                  </span>
                </div>
              </div>
            )}

            <div style={{ marginTop: 32, fontSize: 11, color: 'var(--text-tertiary)', borderTop: '1px solid var(--border)', paddingTop: 12 }}>
              Statement generated {new Date(statement.generatedAt).toLocaleString()} &nbsp;·&nbsp; {statement.accountName}
              &nbsp;·&nbsp; Please remit payment to the address on your invoices.
            </div>
          </div>
        )}
      </div>
    </>
  );
}
